import { useReselector } from '@/utils/sharedHooks'
import { Card, Separator, Text, Table, CardColors, Link } from '@/ui'
import { CurrencyFormatLabel } from '@/components/CurrencyFormatLabel'
import moment from 'moment'
import { DATE_FORMATS } from '@/utils/dateHelpers'
import { selectTransactionsByCategoryAndYear } from '@/features/transactions/transactions.selectors'
import { getTransactionCategorySelector } from '@/features/reports/reports.selectors'
import { TextColors, TypographyStyles } from '@/styles'
import { ActivePayload } from './ExpensesPieChartRow'

export const ExpensesBreakdownCard = ({
  activePayload,
  activeTransactionCategoryId,
  year,
}: {
  activePayload: ActivePayload | null
  activeTransactionCategoryId: number | null
  year: string
}) => {
  const selectedTransactions = useReselector(
    selectTransactionsByCategoryAndYear,
    activeTransactionCategoryId,
    year
  )
  const selectedCategory = useReselector(
    getTransactionCategorySelector,
    activeTransactionCategoryId
  )

  if (activeTransactionCategoryId) {
    return (
      <Card color={CardColors.LEVEL_0}>
        <Text variant={TypographyStyles.H2}>
          Expense Type: {selectedCategory?.name}
        </Text>
        <Separator />
        <div className="flex flex-row justify-between my-6">
          <div className="flex flex-col justify-between">
            <Text variant={TypographyStyles.H1} color={TextColors.BRAND}>
              <CurrencyFormatLabel value={activePayload?.value} />
            </Text>
            <Text variant={TypographyStyles.BODY_SM} className="mt-2">
              Total Dollar Spend
            </Text>
          </div>
          <div>
            <Text variant={TypographyStyles.H1} color={TextColors.DANGER}>
              <b>{Math.trunc((activePayload?.percent ?? 0) * 100)}%</b>
            </Text>
            <Text variant={TypographyStyles.BODY_SM} className="mt-2">
              Percentage of Expenses
            </Text>
          </div>
        </div>
        <Text variant={TypographyStyles.H3} className="mt-8">
          Included Transactions{' '}
        </Text>
        <Table className="w-full border-neutral-200 border-1 rounded-md mt-4">
          <Table.Body>
            {selectedTransactions?.slice(0, 5).map((transaction) => (
              <Table.Row key={transaction.id}>
                <Table.Cell>
                  <Text variant={TypographyStyles.BODY_SM}>
                    {' '}
                    {moment
                      .utc(transaction.date)
                      .format(DATE_FORMATS.DISPLAY_SHORT)}
                  </Text>
                </Table.Cell>
                <Table.Cell>
                  <Text variant={TypographyStyles.BODY_SM}>
                    {transaction.description}
                  </Text>
                </Table.Cell>
                <Table.Cell className="text-right">
                  <Text variant={TypographyStyles.BODY_SM}>
                    <CurrencyFormatLabel
                      value={transaction.amountInCents / 100}
                    />
                  </Text>
                </Table.Cell>
              </Table.Row>
            ))}
            <Table.Row>
              <Table.Cell className="col-span-2">
                <Text
                  variant={TypographyStyles.BODY_SM}
                  color={TextColors.MOSS}
                >
                  Viewing {selectedTransactions?.slice(0, 5).length} out of{' '}
                  {selectedTransactions?.length} transactions
                </Text>
              </Table.Cell>
              <Table.Cell textAlign="right">
                <Link
                  href={`/transactions/expenses/${activeTransactionCategoryId}?year=${year}`}
                  newPage={false}
                >
                  View All
                </Link>
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>
      </Card>
    )
  } else {
    return (
      <Card color={CardColors.LEVEL_1}>
        <Text variant={TypographyStyles.H3}>Expense Type: None Selected</Text>
        <Separator />
        <Text variant={TypographyStyles.BODY_SM}>
          To view a breakdown of a specific expense type, select a category from
          the pie chart to your left.
        </Text>
      </Card>
    )
  }
}
